import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { AxiosError } from 'axios';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { patchUnlimitedSettings } from 'entities/admin/Users/api';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { InputField } from 'shared/ui/InputField';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Paper } from 'shared/ui/Paper';
import { SubscriptionType } from 'entities/Subscriptions/types';
import { Button } from 'shared/ui/Button';

export const unlimitedSettingsValidationSchema = yup.object({
	maxPagesWarningLimit: yup.number(),
});

interface LimitPagesModalProps {
	subscription: SubscriptionType;
	open: boolean;
	handleClose: () => void;
}

const LimitPagesModal: FC<LimitPagesModalProps> = ({ subscription, open, handleClose }) => {
	const { t } = useTranslation();
	const { userId } = useParams();
	const queryClient = useQueryClient();

	const { mutate: patchUnlimitedSettingsMutate } = useMutation({
		mutationKey: ['patch/unlimited-settings'],
		mutationFn: patchUnlimitedSettings,
		onSuccess: async () => {
			await queryClient.refetchQueries({ queryKey: [`admin/users/${userId}/subscriptions`] });
			handleClose();
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const handleManualModalSubmit = (values: yup.InferType<typeof unlimitedSettingsValidationSchema>) => {
		if (userId) {
			patchUnlimitedSettingsMutate({
				userId,
				data: {
					maxPagesWarningLimit: values.maxPagesWarningLimit || null,
				},
			});
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperComponent={Paper}
			PaperProps={{ title: t('shared_texts.limit_pages') as string }}
		>
			<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '34px' }}>
				{t('shared_texts.limit_pages_description')}
			</Typography>
			<Formik
				initialValues={{
					maxPagesWarningLimit: subscription.maxPagesWarningLimit || 300,
				}}
				validationSchema={unlimitedSettingsValidationSchema}
				onSubmit={handleManualModalSubmit}
			>
				<Form>
					<InputField
						name={'maxPagesWarningLimit'}
						label={''}
						placeholder={''}
						required
						type="number"
					/>
					<Button type={'submit'} fullWidth>
						{t('shared_texts.update')}
					</Button>
				</Form>
			</Formik>
		</Dialog>
	);
};

export default LimitPagesModal;
